import * as bitcoin from "bitcoinjs-lib";
import {
  AddressPurposes,
  BitcoinNetwork,
  getAddress,
  signTransaction,
} from "sats-connect";
import { BTC_NETWORK } from "../constants";

type UserAddressType = "p2pkh" | "p2sh-p2wpkh" | "p2wpkh" | "p2tr";

type WalletConnectResponse = {
  ordinalsAddress: string;
  paymentAddress: string;
  ordinalsPubkey: string;
  paymentPubkey: string;
  ordinalsAddressType: string;
};

type WalletSignatureResponse = {
  signedPsbt: string;
};

interface IWalletProvider {
  connect(): Promise<WalletConnectResponse>;
  signTransaction(
    psbtBase64: string,
    inputsSigningConfig?: PsbtSignConfig[]
  ): Promise<WalletSignatureResponse>;
}

export type PsbtSignConfig = {
  address: string;
  indexesToSign: number[];
  sigHash?: number;
};

const XVERSE_BTC_NETWORK: BitcoinNetwork = {
  type: BTC_NETWORK === "mainnet" ? "Mainnet" : "Testnet",
};

const BITCOIN_JS_NETWORK: bitcoin.Network =
  BTC_NETWORK === "mainnet"
    ? bitcoin.networks.bitcoin
    : bitcoin.networks.testnet;

function getUserPaymentType(address: string): UserAddressType {
  let decodedBase58;
  let decodedBech32;
  try {
    decodedBase58 = bitcoin.address.fromBase58Check(address);
  } catch (e) {}

  if (decodedBase58) {
    if (decodedBase58.version === BITCOIN_JS_NETWORK.pubKeyHash) {
      return "p2pkh";
    }
    if (decodedBase58.version === BITCOIN_JS_NETWORK.scriptHash) {
      return "p2sh-p2wpkh";
    }
  } else {
    try {
      decodedBech32 = bitcoin.address.fromBech32(address);
    } catch (e) {}
    if (decodedBech32) {
      if (decodedBech32.version === 0) {
        if (decodedBech32.data.length === 20) {
          return "p2wpkh";
        }
      } else if (decodedBech32.version === 1) {
        if (decodedBech32.data.length === 32) {
          return "p2tr";
        }
      }
    }
  }

  throw new Error("Invalid address");
}

class XverseWalletProvider implements IWalletProvider {
  async connect(): Promise<WalletConnectResponse> {
    return new Promise(async (resolve, reject) => {
      await getAddress({
        payload: {
          purposes: [AddressPurposes.ORDINALS, AddressPurposes.PAYMENT],
          message: "Address for receiving Ordinals and payments",
          network: XVERSE_BTC_NETWORK,
        },
        onFinish: (response) => {
          const ordinals = response.addresses.find(
            (a) => a.purpose === "ordinals"
          );
          const payment = response.addresses.find(
            (a) => a.purpose === "payment"
          );
          if (!ordinals || !payment) {
            return reject(new Error("No addresses returned"));
          }

          resolve({
            ordinalsAddress: ordinals.address,
            paymentAddress: payment.address,
            ordinalsPubkey: ordinals.publicKey,
            paymentPubkey: payment.publicKey,
            ordinalsAddressType: getUserPaymentType(ordinals.address),
          });
        },
        onCancel: () => {
          reject(new Error("Wallet connection cancelled"));
        },
      });
    });
  }

  async signTransaction(
    psbtBase64: string,
    inputsSigningConfig?: PsbtSignConfig[]
  ): Promise<WalletSignatureResponse> {
    return new Promise(async (resolve, reject) => {
      await signTransaction({
        payload: {
          network: XVERSE_BTC_NETWORK,
          message: "Sign Transaction",
          psbtBase64,
          broadcast: false,
          inputsToSign:
            inputsSigningConfig?.map((c) => ({
              address: c.address,
              signingIndexes: c.indexesToSign,
              sigHash: c.sigHash,
            })) ?? [],
        },
        onFinish: (result) => {
          resolve({ signedPsbt: result.psbtBase64 });
        },
        onCancel: () => {
          reject("Transaction signature rejected");
        },
      });
    });
  }
}

class UnisatWalletProvider implements IWalletProvider {
  async connect(): Promise<WalletConnectResponse> {
    const accountAddress = (
      await this.getUnisat().requestAccounts()
    )?.[0] as string;
    const publicKey = (await this.getUnisat().getPublicKey()) as string;
    return Promise.resolve({
      ordinalsAddress: accountAddress,
      ordinalsPubkey: publicKey,
      paymentAddress: accountAddress,
      paymentPubkey: publicKey,
      ordinalsAddressType: getUserPaymentType(accountAddress),
    });
  }

  async signTransaction(
    psbtBase64: string,
    inputsSigningConfig: PsbtSignConfig[]
  ): Promise<WalletSignatureResponse> {
    const psbthex = bitcoin.Psbt.fromBase64(psbtBase64, {
      network: BITCOIN_JS_NETWORK,
    }).toHex();

    const hexString: string = await this.getUnisat().signPsbt(psbthex, {
      autoFinalized: false,
      toSignInputs: inputsSigningConfig
        .map((c) =>
          c.indexesToSign.map((sigIndex) => ({
            address: c.address,
            index: sigIndex,
            sighashTypes: c.sigHash !== undefined ? [c.sigHash] : undefined,
          }))
        )
        .reduce((x, y) => x.concat(y), []),
    });

    return { signedPsbt: bitcoin.Psbt.fromHex(hexString).toBase64() };
  }

  private getUnisat() {
    const unisat: any = (window.top as any)?.unisat;
    if (!unisat) {
      throw new Error("Cannot cannot to Unisat wallet");
    }

    return unisat;
  }
}

export const walletProvider: IWalletProvider =
  window.location.hash === "#xverse"
    ? new XverseWalletProvider()
    : new UnisatWalletProvider();
